import { DashboardData, RawDataItem } from '../../../types';

// カラーパレット
export const COLORS = [
  // 元の6色
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d',
  // 追加の色（明るく区別しやすい色を追加）
  '#e6194B', '#ffe119', '#4363d8', '#3cb44b', '#f58231', '#911eb4',
  '#46f0f0', '#f032e6', '#bcf60c', '#fabebe', '#008080', '#e6beff',
  '#9A6324', '#fffac8', '#800000', '#aaffc3', '#808000', '#ffd8b1',
  '#000075', '#808080', '#a9a9a9', '#4B0082', '#32CD32', '#FF1493',
  '#4682B4', '#9370DB', '#B0E0E6', '#D2691E', '#FF6347', '#7B68EE',
  '#6495ED', '#DC143C', '#00FFFF', '#00FF7F', '#6A5ACD', '#7FFF00'
];

// 指定された順序で結果タイプを設定
export const orderedResultTypes = [
  "アルコールで実施した",
  "石鹸と流水で実施した",
  "実施しなかった"
];

const preferredReasonOrder = [
  '手袋着用',
  '手に荷物あり',
  '意識なし',
  '走っている',
  '手指衛生剤なし',
];

const lastReasonLabel = '該当なし';

const reasonCollator = new Intl.Collator('ja', { numeric: true, sensitivity: 'base' });

export const NON_COMPLIANCE_RESULT = '実施しなかった';
export const UNFILLED_REASON_LABEL = '未入力';
export const UNFILLED_FIELD_LABEL = '未記入';

export const moveLabelToEnd = (keys: string[], label: string): string[] => {
  if (!keys.includes(label)) {
    return [...keys];
  }

  return [...keys.filter(key => key !== label), label];
};

// 指定された順序でタイミングを配置
export const recordWeight = (record: RawDataItem): number =>
  Math.max(0, record.count ?? 1);

export const orderedTimings = [
  "患者に触れる前",
  "患者に触れた後",
  "体液に触れた可能性あり",
  "清潔操作の前",
  "患者周囲環境に触れた後"
];

/**
 * 収集期間、ユニークな日数と部署数を計算
 */
export const calculateDataStats = (data: DashboardData) => {
  if (!data?.raw_data) return { collectionPeriod: null, uniqueDays: 0, uniqueDepartments: 0 };

  if (data.summary_stats) {
    const { firstDate, lastDate, uniqueDays, uniqueDepartments } = data.summary_stats;
    const start = firstDate ? new Date(firstDate) : null;
    const end = lastDate ? new Date(lastDate) : null;
    return {
      collectionPeriod: start && end
        ? {
            start,
            end,
            days: Math.floor((end.getTime() - start.getTime()) / 86400000) + 1,
          }
        : null,
      uniqueDays,
      uniqueDepartments,
    };
  }
  // 日付でソートして期間を取得
  const dates = data.raw_data
    .filter(record => record.date)
    .map(record => new Date(record.date))
    .sort((a, b) => a.getTime() - b.getTime());

  // ユニークな日数
  const uniqueDays = new Set(
    data.raw_data
      .filter(record => record.date)
      .map(record => record.date.split('T')[0]) // 日付部分のみ取得
  ).size;

  // ユニークな部署数
  const uniqueDepartments = new Set(
    data.raw_data
      .filter(record => record.department)
      .map(record => record.department)
  ).size;

  // 期間の計算
  const collectionPeriod = dates.length > 0
    ? {
        start: dates[0],
        end: dates[dates.length - 1],
        days: Math.floor((dates[dates.length - 1].getTime() - dates[0].getTime()) / (1000 * 60 * 60 * 24)) + 1
      }
    : null;

  return { collectionPeriod, uniqueDays, uniqueDepartments };
};

/**
 * 「結果」の種類を取得
 */
export const getContentTypes = (data: DashboardData): string[] => {
  const contentTypes = new Set<string>();
  data.raw_data.forEach(record => {
    if (record.result) {
      contentTypes.add(record.result);
    }
  });
  return Array.from(contentTypes);
};

/**
 * グラフで使用するカラーを生成
 */
export const generateContentColors = (data: DashboardData): Record<string, string> => {
  const contentColorMap: Record<string, string> = {
    "アルコールで実施した": "#00C49F", // 緑色
    "石鹸と流水で実施した": "#FFBB28", // 黄色
    "実施しなかった": "#0088FE"        // 青色
  };

  // 他の内容タイプが存在する場合は、残りの色を割り当て
  const otherContentTypes = getContentTypes(data).filter(
    content => !contentColorMap[content]
  );

  otherContentTypes.forEach((content, index) => {
    const remainingColors = COLORS.filter(
      color => !Object.values(contentColorMap).includes(color)
    );
    contentColorMap[content] = remainingColors[index % remainingColors.length];
  });

  return contentColorMap;
};

/**
 * 部署ごとの結果データを処理
 */
export const getDepartmentContentData = (data: DashboardData) => {
  // 全部署のリスト取得
  const departments = new Set<string>();
  data.raw_data.forEach(record => {
    if (record.department) {
      departments.add(record.department);
    }
  });

  // 部署ごとの結果カウント
  const departmentContentCounts: Record<string, Record<string, number>> = {};

  // 初期化
  Array.from(departments).forEach(dept => {
    departmentContentCounts[dept] = {};
    orderedResultTypes.forEach(content => {
      departmentContentCounts[dept][content] = 0;
    });
  });

  // カウント
  data.raw_data.forEach(record => {
    if (record.department && record.result) {
      departmentContentCounts[record.department][record.result] += recordWeight(record);
    }
  });

  // グラフデータ形式に変換
  return Array.from(departments).map(dept => {
    const result: Record<string, any> = { name: dept || '未記入' };
    let total = 0;

    orderedResultTypes.forEach(content => {
      result[content] = departmentContentCounts[dept][content];
      total += departmentContentCounts[dept][content];
    });

    // 割合（%）に変換
    orderedResultTypes.forEach(content => {
      if (total > 0) {
        result[`${content}Percent`] = (result[content] / total) * 100;
      } else {
        result[`${content}Percent`] = 0;
      }
    });

    return result;
  })
  .sort((a, b) => {
    // 部署名を数値部分と文字部分に分離してソート
    const nameA = a.name || '';
    const nameB = b.name || '';

    // 正規表現で文字部分と数字部分を抽出
    const regexResult1 = nameA.match(/^([A-Za-z]+)(\d+)$/);
    const regexResult2 = nameB.match(/^([A-Za-z]+)(\d+)$/);

    // マッチしなかった場合（通常の文字列）は通常のソート
    if (!regexResult1 || !regexResult2) {
      return nameA.localeCompare(nameB);
    }

    // 文字部分と数字部分を取得
    const [, letterA, numberA] = regexResult1;
    const [, letterB, numberB] = regexResult2;

    // まず文字部分で比較
    const letterCompare = letterA.localeCompare(letterB);
    if (letterCompare !== 0) {
      return letterCompare;
    }

    // 文字部分が同じなら、数字部分を数値として比較
    return parseInt(numberA, 10) - parseInt(numberB, 10);
  }); // 部署名でソート
};

/**
 * 職種ごとの結果データを処理
 */
export const getOccupationContentData = (data: DashboardData) => {
  if (!data?.raw_data) return [];

  const occupationContentCounts: Record<string, Record<string, number>> = {};
  const occupations = new Set<string>();

  // 生データからすべての職種を取得し、カウントオブジェクトを初期化
  data.raw_data.forEach(record => {
    const occupation = record.occupation || '未記入';
    occupations.add(occupation);
  });

  occupations.forEach(occ => {
    occupationContentCounts[occ] = {};
    orderedResultTypes.forEach(type => {
      occupationContentCounts[occ][type] = 0;
    });
  });

  // 生データから結果をカウント
  data.raw_data.forEach(record => {
    if (record.occupation && record.result) {
      const occupation = record.occupation || '未記入';
      if (occupationContentCounts[occupation]) {
        occupationContentCounts[occupation][record.result] += recordWeight(record);
      }
    }
  });
  
  // グラフデータ形式に変換
  return Array.from(occupations).map(occupation => {
    const result: Record<string, any> = { name: occupation };
    let total = 0;

    orderedResultTypes.forEach(content => {
      result[content] = occupationContentCounts[occupation][content] || 0;
      total += result[content];
    });

    // 割合（%）に変換
    orderedResultTypes.forEach(content => {
      if (total > 0) {
        result[`${content}Percent`] = (result[content] / total) * 100;
      } else {
        result[`${content}Percent`] = 0;
      }
    });

    return result;
  })
  .sort((a, b) => (a.name || '').localeCompare(b.name || '')); // 職種名でソート
};

/**
 * タイミングごとの結果データを処理
 */
export const getTimingContentData = (data: DashboardData) => {
  if (!data?.raw_data) return [];

  // タイミングごとの結果カウント用オブジェクト初期化
  const timingContentCounts: Record<string, Record<string, number>> = {};

  // すべてのタイミングを初期化
  orderedTimings.forEach(timing => {
    timingContentCounts[timing] = {};
    orderedResultTypes.forEach(content => {
      timingContentCounts[timing][content] = 0;
    });
  });

  // 生データからカウントを取得
  data.raw_data.forEach(record => {
    if (record.timing && record.result && orderedTimings.includes(record.timing)) {
      timingContentCounts[record.timing][record.result] += recordWeight(record);
    }
  });
  
  // 以降のデータ整形ロジックは変更なし（元のコードをそのまま利用）
  const result = orderedTimings
    .filter(timing => {
      const totalCount = Object.values(timingContentCounts[timing] || {})
                                .reduce((sum, count) => sum + (count || 0), 0);
      return totalCount > 0;
    })
    .map(timing => {
      const resultObj: Record<string, any> = {
        name: timing,
        timingValue: timing
      };
      const total = Object.values(timingContentCounts[timing] || {})
                          .reduce((sum, count) => sum + (count || 0), 0);
      orderedResultTypes.forEach(content => {
        resultObj[content] = timingContentCounts[timing][content] || 0;
      });
      if (total > 0) {
        orderedResultTypes.forEach((content) => {
          const count = timingContentCounts[timing][content] || 0;
          const percent = Math.round((count / total) * 1000) / 10;
          resultObj[`${content}Percent`] = percent;
          resultObj[`${content}Count`] = count;
        });
      } else {
        orderedResultTypes.forEach(content => {
          resultObj[`${content}Percent`] = 0;
          resultObj[`${content}Count`] = 0;
        });
      }
      return resultObj;
    });

  return result;
};

/**
 * 結果データの加工
 */
export const getResultData = (data: DashboardData) => {
  // 結果別の集計を計算
  const resultCounts: Record<string, number> = {};
  orderedResultTypes.forEach(result => {
    resultCounts[result] = data.raw_data
      .filter(record => record.result === result)
      .reduce((sum, record) => sum + recordWeight(record), 0);
  });
  return orderedResultTypes.map(name => ({
    name,
    value: resultCounts[name] || 0,
  }));
};

export const hasFilledNonComplianceReasons = (rawData: RawDataItem[]) => {
  return rawData.some(
    record =>
      record.result === NON_COMPLIANCE_RESULT &&
      normalizeReason(record.reason) !== UNFILLED_REASON_LABEL
  );
};

export const normalizeReason = (reason?: string) => reason?.trim() || UNFILLED_REASON_LABEL;
export const normalizeField = (value?: string) => value?.trim() || UNFILLED_FIELD_LABEL;

const getReasonPriority = (reasonName: string) => {
  const preferredIndex = preferredReasonOrder.indexOf(reasonName);
  if (preferredIndex !== -1) return preferredIndex;
  if (reasonName === lastReasonLabel) return preferredReasonOrder.length + 1;
  if (reasonName === UNFILLED_REASON_LABEL) return preferredReasonOrder.length + 2;
  return preferredReasonOrder.length;
};

const sortReasonsByPreference = <T extends { name: string }>(reasons: T[]) =>
  [...reasons].sort((a, b) => {
    const priorityA = getReasonPriority(a.name);
    const priorityB = getReasonPriority(b.name);

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    return reasonCollator.compare(a.name, b.name);
  });

export const getNonComplianceReasonData = (
  data: DashboardData,
  options?: { includeEmpty?: boolean; topN?: number }
) => {
  const reasonCounts: Record<string, number> = {};
  const includeEmpty = options?.includeEmpty ?? true;

  data.raw_data.forEach(record => {
    if (record.result === NON_COMPLIANCE_RESULT) {
      const reason = normalizeReason(record.reason);
      if (!includeEmpty && reason === UNFILLED_REASON_LABEL) return;
      reasonCounts[reason] = (reasonCounts[reason] || 0) + recordWeight(record);
    }
  });

  const entries = Object.entries(reasonCounts)
    .map(([name, value]) => ({ name, value }));

  const sortedByCount = [...entries].sort((a, b) => b.value - a.value);
  const baseList = options?.topN ? sortedByCount.slice(0, options.topN) : entries;
  const sorted = sortReasonsByPreference(baseList);

  if (options?.topN) {
    return sorted;
  }

  return sorted;
};

export const getReasonInputStats = (data: DashboardData) => {
  const totalNonCompliance = data.raw_data
    .filter(record => record.result === NON_COMPLIANCE_RESULT)
    .reduce((sum, record) => sum + recordWeight(record), 0);
  const withReasonCount = data.raw_data
    .filter(record => record.result === NON_COMPLIANCE_RESULT && !!record.reason?.trim())
    .reduce((sum, record) => sum + recordWeight(record), 0);
  const withoutReasonCount = totalNonCompliance - withReasonCount;

  const reasonInputRate = totalNonCompliance > 0
    ? Math.round((withReasonCount / totalNonCompliance) * 1000) / 10
    : 0;

  return {
    totalNonCompliance,
    withReasonCount,
    withoutReasonCount,
    reasonInputRate,
  };
};

export type ReasonAggregationGrouping =
  | 'department'
  | 'occupation'
  | 'timing'
  | 'departmentOccupation'
  | 'departmentTiming'
  | 'departmentOccupationTiming';

export interface ReasonAggregationRow extends Record<string, string | number> {
  name: string;
  total: number;
}

export interface ReasonAggregation {
  data: ReasonAggregationRow[];
  reasons: string[];
}

const getGroupLabel = (record: RawDataItem, grouping: ReasonAggregationGrouping) => {
  switch (grouping) {
    case 'department':
      return normalizeField(record.department);
    case 'occupation':
      return normalizeField(record.occupation);
    case 'timing':
      return normalizeField(record.timing);
    case 'departmentOccupation':
      return `${normalizeField(record.department)}×${normalizeField(record.occupation)}`;
    case 'departmentTiming':
      return `${normalizeField(record.department)}×${normalizeField(record.timing)}`;
    case 'departmentOccupationTiming':
      return `${normalizeField(record.department)}×${normalizeField(record.occupation)}×${normalizeField(record.timing)}`;
    default:
      return UNFILLED_FIELD_LABEL;
  }
};

export const aggregateReasonsByGrouping = (
  data: DashboardData,
  grouping: ReasonAggregationGrouping,
  reasonOrder?: string[],
): ReasonAggregation => {
  const groupedCounts: Record<string, Record<string, number>> = {};
  const totalReasons: Record<string, number> = {};

  data.raw_data.forEach(record => {
    if (record.result !== NON_COMPLIANCE_RESULT) return;
    const groupLabel = getGroupLabel(record, grouping);
    const reasonLabel = normalizeReason(record.reason);

    if (!groupedCounts[groupLabel]) {
      groupedCounts[groupLabel] = {};
    }
    const weight = recordWeight(record);
    groupedCounts[groupLabel][reasonLabel] =
      (groupedCounts[groupLabel][reasonLabel] || 0) + weight;
    totalReasons[reasonLabel] = (totalReasons[reasonLabel] || 0) + weight;
  });

  const orderedReasons = reasonOrder && reasonOrder.length > 0
    ? reasonOrder
    : sortReasonsByPreference(
        Object.entries(totalReasons).map(([name, value]) => ({ name, value }))
      ).map(({ name }) => name);

  const dataRows: ReasonAggregationRow[] = Object.entries(groupedCounts).map(([groupName, counts]) => {
    const row: ReasonAggregationRow = { name: groupName, total: 0 };
    orderedReasons.forEach(reason => {
      row[reason] = counts[reason] || 0;
    });
    row.total = Object.values(counts).reduce((sum, count) => sum + count, 0);
    return row;
  })
  .sort((a, b) => b.total - a.total);

  return {
    data: dataRows,
    reasons: orderedReasons,
  };
};

/**
 * 職種データの加工
 */
export const getOccupationData = (data: DashboardData) => {
  // data.raw_dataから職種ごとの件数を集計する
  const occupationCounts: { [key: string]: number } = {};
  if (data && data.raw_data) {
    data.raw_data.forEach(record => {
      const occupation = record.occupation || '未記入';
      occupationCounts[occupation] = (occupationCounts[occupation] || 0) + recordWeight(record);
    });
  }

  // 集計結果をグラフで使える形式に変換
  return Object.entries(occupationCounts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value); // 件数の多い順にソート
};

/**
 * タイミングデータの加工
 */
export const getTimingData = (data: DashboardData) => {
  if (!data?.raw_data) return [];

  // タイミング別の集計を計算
  const timingCounts: Record<string, number> = {};
  data.raw_data.forEach(record => {
    if (record.timing) {
      timingCounts[record.timing] = (timingCounts[record.timing] || 0) + recordWeight(record);
    }
  });

  // データを指定順序で並べ替え
  return orderedTimings
    .filter(timing => timingCounts[timing] !== undefined)
    .map(timing => ({
      name: timing,
      value: timingCounts[timing] || 0
    }))
    .concat(
      // 指定順序に含まれないものを追加
      Object.entries(timingCounts)
        .filter(([timing]) => !orderedTimings.includes(timing))
        .map(([timing, count]) => ({
          name: timing,
          value: count
        }))
    )
    .sort((a, b) => b.value - a.value); // 件数の多い順にソート
};
